/* ==========================================================================
   Premium Product Grid  ·  assets/premium-product-grid.js
   --------------------------------------------------------------------------
   Behaviour for sections/premium-product-grid.liquid.

   • AJAX add-to-cart via Shopify's native /cart/add.js (no redirect).
   • Integrates with an existing cart drawer / notification when present
     (getSectionsToRender + renderContents), and always dispatches events
     so any cart UI can react.
   • Minimal wishlist: UI state + localStorage stub, structured so a real
     backend can be wired in later.

   Vanilla JS. One delegated listener per section. No external libraries,
   no jQuery, no polling, no MutationObserver.
   ========================================================================== */

if (!customElements.get('premium-product-grid')) {
  customElements.define(
    'premium-product-grid',
    class PremiumProductGrid extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
        this._wishlistKey = 'ppg:wishlist';
      }

      connectedCallback() {
        // One delegated click listener handles every card's buttons.
        this.addEventListener('click', this._onClick);
        this._restoreWishlistState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
      }

      /* -------------------------------------------------------------------- */
      /*  Delegated click routing                                             */
      /* -------------------------------------------------------------------- */
      _onClick(event) {
        const addBtn = event.target.closest('[data-ppg-add]');
        if (addBtn) {
          event.preventDefault();
          this._addToCart(addBtn);
          return;
        }

        const wishBtn = event.target.closest('[data-ppg-wishlist]');
        if (wishBtn) {
          event.preventDefault();
          this._toggleWishlist(wishBtn);
        }
      }

      /* -------------------------------------------------------------------- */
      /*  Add to cart                                                         */
      /* -------------------------------------------------------------------- */
      _addToCart(button) {
        // Prevent duplicate rapid submissions.
        if (button.dataset.loading === 'true' || button.hasAttribute('disabled')) return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.dataset.loading = 'true';
        button.classList.remove('is-success', 'is-error');
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');

        const cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        // If the theme's cart element can render sections, ask for them so the
        // drawer/notification updates exactly as it would from a product form.
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        const addUrl =
          (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            // Shopify returns `status` on error (e.g. sold out / not enough stock).
            if (data.status) {
              this._showError(button, data.description || data.message);
              return;
            }
            this._showSuccess(button);
            this._notifyCart(cart, data, variantId);
          })
          .catch(() => {
            this._showError(button);
          })
          .finally(() => {
            button.dataset.loading = 'false';
            button.classList.remove('is-loading');
            button.removeAttribute('aria-disabled');
          });
      }

      // Update / open the existing cart UI and broadcast events.
      //
      // ORDER MATTERS. We must broadcast BEFORE renderContents — the same order
      // the theme's own product form uses. Some themes (this one included) have a
      // `cart-drawer-items` whose `cartUpdate` subscriber asynchronously re-renders
      // the drawer items from the cart-PAGE section, which drops #CartDrawer-Form
      // and orphans the Check out button. By publishing first and calling
      // renderContents last, renderContents synchronously replaces the drawer node,
      // so that stray async re-render lands on a detached node and is harmless.
      _notifyCart(cart, data, variantId) {
        // 1) Theme pub/sub bus (Dawn-style), if present — FIRST.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'premium-product-grid',
              productVariantId: variantId,
            });
          }
        } catch (e) {
          /* pub/sub not available — non-fatal */
        }

        // 2) Generic DOM event any custom cart UI can listen for — FIRST.
        document.dispatchEvent(
          new CustomEvent('ppg:item-added', {
            bubbles: true,
            detail: { variantId: variantId, cart: data },
          })
        );

        // 3) Render / open the cart drawer LAST so its complete markup
        //    (including #CartDrawer-Form) is the final state and wins.
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
          // Clear the host empty-state AFTER render (matches the theme's product form).
          if (cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
        } else {
          // No known cart element → best-effort cart-count bubble refresh.
          this._refreshCartCount();
        }
      }

      _refreshCartCount() {
        fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        })
          .then((response) => response.json())
          .then((cart) => {
            const count = cart.item_count;
            document
              .querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]')
              .forEach((el) => {
                el.textContent = count;
              });
          })
          .catch(() => {
            /* non-fatal */
          });
      }

      _showSuccess(button) {
        const label = button.querySelector('[data-ppg-add-label]') || button;
        const original = button.dataset.labelDefault || label.textContent;
        button.dataset.labelDefault = original;
        button.classList.add('is-success');
        label.textContent = button.dataset.labelSuccess || 'Added to Bag ✓';
        clearTimeout(this._successTimer);
        this._successTimer = setTimeout(() => {
          button.classList.remove('is-success');
          label.textContent = original;
        }, 2000);
      }

      _showError(button, message) {
        const label = button.querySelector('[data-ppg-add-label]') || button;
        const original = button.dataset.labelDefault || label.textContent;
        button.dataset.labelDefault = original;
        button.classList.add('is-error');
        label.textContent = button.dataset.labelError || 'Try again';
        if (message) button.setAttribute('title', message);
        clearTimeout(this._errorTimer);
        this._errorTimer = setTimeout(() => {
          button.classList.remove('is-error');
          label.textContent = original;
          button.removeAttribute('title');
        }, 2500);
      }

      /* -------------------------------------------------------------------- */
      /*  Wishlist (UI + localStorage stub — backend-ready)                   */
      /* -------------------------------------------------------------------- */
      _toggleWishlist(button) {
        const id = button.dataset.productId;
        const list = this._getWishlist();
        const index = list.indexOf(id);
        const nowActive = index === -1;

        if (nowActive) {
          list.push(id);
        } else {
          list.splice(index, 1);
        }
        this._saveWishlist(list);
        button.setAttribute('aria-pressed', String(nowActive));

        // Hook for a future wishlist backend / app.
        document.dispatchEvent(
          new CustomEvent('ppg:wishlist-changed', {
            bubbles: true,
            detail: { productId: id, active: nowActive, wishlist: list },
          })
        );
      }

      _restoreWishlistState() {
        const list = this._getWishlist();
        if (!list.length) return;
        this.querySelectorAll('[data-ppg-wishlist]').forEach((button) => {
          if (list.indexOf(button.dataset.productId) !== -1) {
            button.setAttribute('aria-pressed', 'true');
          }
        });
      }

      _getWishlist() {
        try {
          return JSON.parse(window.localStorage.getItem(this._wishlistKey)) || [];
        } catch (e) {
          return [];
        }
      }

      _saveWishlist(list) {
        try {
          window.localStorage.setItem(this._wishlistKey, JSON.stringify(list));
        } catch (e) {
          /* storage unavailable (private mode) — UI state still works this session */
        }
      }
    }
  );
}
